import { useState, useRef, useEffect, useCallback } from 'react';
import { Filter, Info } from 'lucide-react';

export type DateRange = 'yesterday' | '7d' | '30d' | '60d' | '90d' | 'all' | 'custom';

export interface DateFilter {
  range: DateRange;
  customStart?: string;
  customEnd?: string;
}

const PRESETS: { key: DateRange; label: string }[] = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
  { key: '60d', label: '60 Days' },
  { key: '90d', label: '90 Days' },
  { key: 'all', label: 'All Time' },
];

const SHORT_LABELS: Record<DateRange, string> = {
  yesterday: 'Yesterday',
  '7d': '7D',
  '30d': '30D',
  '60d': '60D',
  '90d': '90D',
  all: 'All',
  custom: 'Custom',
};

export function getFilterDates(filter: DateFilter): { start: string | null; end: string | null } {
  const now = new Date();

  switch (filter.range) {
    case 'yesterday': {
      const yStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const yEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { start: yStart.toISOString(), end: yEnd.toISOString() };
    }
    case '7d':
      return { start: new Date(now.getTime() - 7 * 86_400_000).toISOString(), end: null };
    case '30d':
      return { start: new Date(now.getTime() - 30 * 86_400_000).toISOString(), end: null };
    case '60d':
      return { start: new Date(now.getTime() - 60 * 86_400_000).toISOString(), end: null };
    case '90d':
      return { start: new Date(now.getTime() - 90 * 86_400_000).toISOString(), end: null };
    case 'custom':
      return {
        start: filter.customStart ? new Date(filter.customStart + 'T00:00:00').toISOString() : null,
        end: filter.customEnd ? new Date(filter.customEnd + 'T23:59:59.999').toISOString() : null,
      };
    default:
      return { start: null, end: null };
  }
}

export function getRangeStart(range: DateRange): string | null {
  return getFilterDates({ range }).start;
}

interface Props {
  value: DateFilter;
  onChange: (filter: DateFilter) => void;
}

export default function DateRangeSelector({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, close]);

  const displayLabel =
    value.range === 'custom' && value.customStart
      ? formatCustomLabel(value.customStart, value.customEnd)
      : SHORT_LABELS[value.range];

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-1.5">
        <div
          className="relative"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <Info size={13} className="cursor-help transition-colors" style={{ color: 'var(--dash-text-faint)' }} />
          {showTooltip && (
            <div
              className="absolute top-full right-0 mt-2 w-52 px-3 py-2.5 rounded text-[11px] font-sans leading-relaxed shadow-xl z-[60] pointer-events-none"
              style={{ background: 'var(--dash-overlay)', border: '1px solid var(--dash-border-solid)', color: 'var(--dash-text-tertiary)' }}
            >
              <div className="absolute top-0 right-2.5 -translate-y-1/2 rotate-45 w-2 h-2" style={{ background: 'var(--dash-overlay)', borderLeft: '1px solid var(--dash-border-solid)', borderTop: '1px solid var(--dash-border-solid)' }} />
              Filter data by time period. Select a preset range or define custom start and end dates.
            </div>
          )}
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded font-mono text-xs transition-all"
          style={{
            border: `1px solid ${open ? 'var(--dash-active-border)' : 'var(--dash-border)'}`,
            background: open ? 'var(--dash-active-bg)' : 'var(--dash-input)',
            color: open ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
          }}
        >
          <Filter size={11} />
          <span>{displayLabel}</span>
        </button>
      </div>

      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 w-56 rounded-md shadow-2xl z-50 overflow-hidden"
          style={{ background: 'var(--dash-dropdown)', border: '1px solid var(--dash-border)' }}
        >
          <div className="py-1">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => {
                  onChange({ range: p.key });
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 font-mono text-xs transition-colors flex items-center justify-between"
                style={{
                  background: value.range === p.key ? 'var(--dash-active-bg)' : undefined,
                  color: value.range === p.key ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
                }}
                onMouseEnter={(e) => { if (value.range !== p.key) { e.currentTarget.style.background = 'var(--dash-hover-bg)'; e.currentTarget.style.color = 'var(--dash-text-secondary)'; } }}
                onMouseLeave={(e) => { if (value.range !== p.key) { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--dash-text-muted)'; } }}
              >
                <span>{p.label}</span>
                {value.range === p.key && (
                  <span className="w-1 h-1 rounded-full" style={{ background: 'var(--dash-text-primary)' }} />
                )}
              </button>
            ))}
          </div>

          <div className="py-1" style={{ borderTop: '1px solid var(--dash-border)' }}>
            <button
              onClick={() => {
                if (value.range !== 'custom') {
                  onChange({ range: 'custom', customStart: value.customStart, customEnd: value.customEnd });
                }
              }}
              className="w-full text-left px-3 py-2 font-mono text-xs transition-colors flex items-center justify-between"
              style={{
                background: value.range === 'custom' ? 'var(--dash-active-bg)' : undefined,
                color: value.range === 'custom' ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
              }}
            >
              <span>Custom Range</span>
              {value.range === 'custom' && (
                <span className="w-1 h-1 rounded-full" style={{ background: 'var(--dash-text-primary)' }} />
              )}
            </button>

            {value.range === 'custom' && (
              <div className="px-3 pb-2.5 pt-1 space-y-2">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-muted)' }}>
                    From
                  </label>
                  <input
                    type="date"
                    value={value.customStart || ''}
                    onChange={(e) => onChange({ ...value, customStart: e.target.value })}
                    className="w-full px-2 py-1.5 rounded font-mono text-xs focus:outline-none transition-colors"
                    style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-secondary)' }}
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-muted)' }}>
                    To
                  </label>
                  <input
                    type="date"
                    value={value.customEnd || ''}
                    onChange={(e) => onChange({ ...value, customEnd: e.target.value })}
                    className="w-full px-2 py-1.5 rounded font-mono text-xs focus:outline-none transition-colors"
                    style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-secondary)' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatCustomLabel(start: string, end?: string): string {
  const fmt = (d: string) => {
    const parts = d.split('-');
    return `${parts[1]}/${parts[2]}`;
  };
  if (end) return `${fmt(start)} \u2013 ${fmt(end)}`;
  return `From ${fmt(start)}`;
}
